import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageHeader from '@/shared/layout/PageHeader';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppSelector, useAppDispatch} from '@/shared/hooks/redux';
import {useTestsState} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {
    selectCurrentPatient,
    selectSubPatients,
    setCurrentPatient,
    restoreCurrentPatientFromStorage,
} from '@/slices/patientSlice';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import {
    determineStatus,
    parseRefRangeWithDefault,
    formatTestName,
    parseNumericValue,
} from '@/shared/utils/biomarkerUtils';
import {severityTier} from '@/shared/biomarkers/classify';
import {getHistoricalData, getFilterDisplayName} from '@/shared/utils/resultUtils';
import {getTestEducation} from '@/shared/data/testEducation';
import BiomarkerChartCard from '../components/BiomarkerChartCard';
import Icon from '@/shared/components/Icon';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';

type StatusFilter = 'all' | 'within-range' | 'needs-attention' | 'high-priority';
type SortOption = 'newest' | 'oldest' | 'alphabetical' | 'status';

const AllBiomarkersResults: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {user} = useAuth();
    const dispatch = useAppDispatch();
    const {rules} = useDisplayRules();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Get status filter from URL parameter
    const statusParam = searchParams.get('status') as StatusFilter | null;
    const [filterBy, setFilterBy] = useState<StatusFilter>(
        statusParam && ['within-range', 'needs-attention', 'high-priority', 'all'].includes(statusParam)
            ? statusParam
            : 'all'
    );
    const [sortBy, setSortBy] = useState<SortOption>('status');
    const [filterOpen, setFilterOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);

    // Redux selectors for current patient and test data
    const currentPatient = useAppSelector(selectCurrentPatient);
    const subPatients = useAppSelector(selectSubPatients);
    const testsState = useTestsState();

    // Always the patient's real data — never gated by membership.
    const {data: tests} = useDashboardData();

    // Restore current patient from localStorage on page load
    useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && user?.token) {
            dispatch(restoreCurrentPatientFromStorage());
        }
    }, [dispatch, currentPatient?.id, user?.token]);

    // Set current patient from subPatients data
    useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && subPatients.length > 0) {
            let targetPatient: any = null;

            try {
                const storedPatient = localStorage.getItem('currentPatient');
                if (storedPatient) {
                    const parsed = JSON.parse(storedPatient);
                    targetPatient = subPatients.find(
                        (p) =>
                            p.id === parsed.id || (p.firstName === parsed.firstName && p.lastName === parsed.lastName)
                    );
                }
            } catch {
                // Silently handle localStorage read errors
            }

            if (!targetPatient) {
                targetPatient = subPatients.find((p) => p.id && p.id > 0 && p.firstName && p.lastName);
            }

            if (targetPatient) {
                dispatch(setCurrentPatient(targetPatient));
            }
        }
    }, [dispatch, currentPatient, subPatients]);

    // Aggregate all biomarkers from all tests
    const allBiomarkers = useMemo(() => {
        const biomarkers: any[] = [];
        const biomarkerMap = new Map<string, any>();

        // Flatten all results from all tests
        const allResults = tests.flatMap((test: any) => test.results || []);

        // Filter to valid results
        const validResults = allResults.filter(
            (result: any) =>
                result.statusDate &&
                result.result &&
                result.test_name &&
                result.result !== 'Pending' &&
                result.result !== 'N/A'
        );

        // Sort by statusDate to get latest first
        const sortedResults = [...validResults].sort(
            (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
        );

        // Keep only the latest result for each test_name
        sortedResults.forEach((result) => {
            if (!biomarkerMap.has(result.test_name)) {
                const status = determineStatus(result, gender, rules);

                // Categorize into our three buckets via the canonical severity
                // tier so the bucket color always agrees with the chip/chart
                // colors: normal→within-range, critical→high-priority,
                // borderline/unknown→needs-attention.
                const tier = severityTier(status);
                const category: 'within-range' | 'needs-attention' | 'high-priority' =
                    tier === 'normal' ? 'within-range' : tier === 'critical' ? 'high-priority' : 'needs-attention';

                biomarkerMap.set(result.test_name, {
                    ...result,
                    displayName: formatTestName(result.test_name),
                    status: status,
                    category: category,
                    testDate: new Date(result.statusDate),
                });
            }
        });

        biomarkerMap.forEach((value) => biomarkers.push(value));
        return biomarkers;
    }, [tests, gender, rules]);

    // Apply filtering
    const filteredBiomarkers = useMemo(() => {
        if (filterBy === 'all') return allBiomarkers;

        return allBiomarkers.filter((b) => b.category === filterBy);
    }, [allBiomarkers, filterBy]);

    // Apply sorting
    const sortedBiomarkers = useMemo(() => {
        const sorted = [...filteredBiomarkers];

        switch (sortBy) {
            case 'newest':
                return sorted.sort((a, b) => b.testDate.getTime() - a.testDate.getTime());
            case 'oldest':
                return sorted.sort((a, b) => a.testDate.getTime() - b.testDate.getTime());
            case 'alphabetical':
                return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
            case 'status':
                return sorted.sort((a, b) => {
                    const statusOrder = {'high-priority': 0, 'needs-attention': 1, 'within-range': 2};
                    return (
                        (statusOrder[a.category as keyof typeof statusOrder] || 2) -
                        (statusOrder[b.category as keyof typeof statusOrder] || 2)
                    );
                });
            default:
                return sorted;
        }
    }, [filteredBiomarkers, sortBy]);

    // Get display text for filter
    const getFilterDisplayText = () => {
        switch (filterBy) {
            case 'all':
                return 'All Biomarkers';
            case 'within-range':
                return 'Within Range';
            case 'needs-attention':
                return 'Needs Attention';
            case 'high-priority':
                return 'High Priority';
            default:
                return 'Filter By';
        }
    };

    // Get display text for sort
    const getSortDisplayText = () => {
        switch (sortBy) {
            case 'newest':
                return 'Newest First';
            case 'oldest':
                return 'Oldest First';
            case 'alphabetical':
                return 'Alphabetical';
            case 'status':
                return 'By Priority';
            default:
                return 'Sort By';
        }
    };

    // Get page title based on filter
    const getPageTitle = () => {
        switch (filterBy) {
            case 'within-range':
                return 'Within Range Biomarkers';
            case 'needs-attention':
                return 'Needs Attention Biomarkers';
            case 'high-priority':
                return 'High Priority Biomarkers';
            default:
                return 'All Biomarkers';
        }
    };

    // Render a biomarker card
    const renderBiomarkerCard = (biomarker: any, index: number) => {
        // `null` for a non-numeric result (e.g. "No Result"). The card renders
        // the verbatim text, hides the slider dot, and gaps the trend for null.
        const value = parseNumericValue(biomarker.result);
        const refRange = parseRefRangeWithDefault(biomarker.refRange);
        // Use the status from determineStatus. Only recalculate Unknown into
        // Low/High/Normal when we actually have a numeric value — a non-numeric
        // result must stay neutral Unknown, never a false Low/High from a
        // coerced 0.
        let status = biomarker.status;
        if (status === 'Unknown' && value !== null && refRange.min !== undefined && refRange.max !== undefined) {
            // Recalculate status based on value
            if (value < refRange.min) {
                status = 'Low';
            } else if (value > refRange.max) {
                status = 'High';
            } else {
                status = 'Normal';
            }
        }
        const education = getTestEducation(biomarker.test_name);

        const uniqueKey = `biomarker-${biomarker.test_name}-${value}-${biomarker.statusDate || 'no-date'}-idx${index}`;

        return (
            <BiomarkerChartCard
                key={uniqueKey}
                name={biomarker.displayName}
                value={value}
                units={biomarker.units || ''}
                status={status}
                rangeMin={refRange.min}
                rangeMax={refRange.max}
                history={getHistoricalData(biomarker.test_name, value, true, tests, true)}
                lastTestDate={biomarker.statusDate}
                education={education}
                result={biomarker}
                gender={gender}
                rules={rules}
            />
        );
    };

    // Show loading state while data is being fetched
    if (testsState.loading) {
        return (
            <DashboardLayout>
                <PageHeader title="All Biomarkers" subtitle="Loading your biomarker results..." />
                <div className="flex items-center justify-center py-12">
                    <Icon name="spinner" spin className="h-8 w-8 text-mld-primary" />
                    <span className="ml-2 text-mld-text-primary">Loading your biomarkers...</span>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="relative py-3 lg:py-6">
                <div className="px-3 lg:px-6">
                    {/* Back button */}
                    <div className="mb-4">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center text-mld-primary hover:text-mld-primary/80 transition-colors cursor-pointer">
                            <Icon name="chevron-left" className="h-5 w-5 mr-1" />
                            Back to Dashboard
                        </button>
                    </div>

                    {/* Header with controls */}
                    <div className="py-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div>
                                <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">{getPageTitle()}</h1>
                                <p className="text-sm text-gray-500 mt-1">
                                    {sortedBiomarkers.length} biomarker{sortedBiomarkers.length !== 1 ? 's' : ''} found
                                </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                                {/* Filter Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            setFilterOpen(!filterOpen);
                                            setSortOpen(false);
                                        }}
                                        className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mld-primary">
                                        <span>{getFilterDisplayText()}</span>
                                        <Icon name="chevron-down" className="h-3 w-3 lg:h-4 lg:w-4" />
                                    </button>

                                    {filterOpen && (
                                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                            <div className="py-1" role="menu">
                                                <button
                                                    onClick={() => {
                                                        setFilterBy('all');
                                                        setFilterOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    All Biomarkers
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setFilterBy('within-range');
                                                        setFilterOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    Within Range
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setFilterBy('needs-attention');
                                                        setFilterOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    Needs Attention
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setFilterBy('high-priority');
                                                        setFilterOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    High Priority
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Sort Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            setSortOpen(!sortOpen);
                                            setFilterOpen(false);
                                        }}
                                        className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mld-primary">
                                        <span>{getSortDisplayText()}</span>
                                        <Icon name="chevron-down" className="h-3 w-3 lg:h-4 lg:w-4" />
                                    </button>

                                    {sortOpen && (
                                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                            <div className="py-1" role="menu">
                                                <button
                                                    onClick={() => {
                                                        setSortBy('status');
                                                        setSortOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    By Priority
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSortBy('newest');
                                                        setSortOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    Newest First
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSortBy('oldest');
                                                        setSortOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    Oldest First
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSortBy('alphabetical');
                                                        setSortOpen(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    Alphabetical (A-Z)
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Display */}
                <div className="px-3 lg:px-6">
                    {sortedBiomarkers.length > 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 lg:p-6">
                            <div className="space-y-3">
                                {sortedBiomarkers.map((biomarker, index) => renderBiomarkerCard(biomarker, index))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg
                                    className="mx-auto h-12 w-12"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No biomarkers found for {getFilterDisplayName(filterBy)}
                            </h3>
                            <p className="text-gray-500 mb-4">
                                Try adjusting your filter settings to see more biomarkers.
                            </p>
                            {filterBy !== 'all' && (
                                <button
                                    onClick={() => setFilterBy('all')}
                                    className="text-sm text-mld-primary hover:text-mld-primary/80 font-medium">
                                    Show all biomarkers
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AllBiomarkersResults;
